import React, { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useLanguage } from '../hooks/useLanguage'
import Header from '../components/landing/Header'
import Footer from '../components/landing/Footer'
import CheckmarkIcon from '../components/icons/CheckmarkIcon'

const AboutPage: React.FC = () => {
  const { t } = useLanguage()

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <div className="relative bg-gradient-to-r from-blue-600 to-blue-800 text-white">
        <div className="absolute inset-0 bg-black bg-opacity-40"></div>
        <div className="relative container mx-auto px-4 py-20">
          <div className="text-center">
            <h1 className="text-5xl font-bold mb-6">{t('aboutUs.title')}</h1>
            <p className="text-xl max-w-3xl mx-auto">
              {t('aboutUs.subtitle')}
            </p>
          </div>
        </div>
      </div>

      {/* Breadcrumbs */}
      <div className="bg-gray-50 border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center space-x-2">
            <Link to="/" className="text-gray-500 hover:text-blue-600">{t('common.home')}</Link>
            <span className="text-gray-400">/</span>
            <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-medium">
              {t('aboutUs.breadcrumb')}
            </span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-16">
        
        {/* Our Story Section */}
        <section className="mb-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="flex justify-center lg:col-span-2">
              <img 
                src="/logo.ico" 
                alt="La Dolce Vita Reis" 
                className="w-96 h-96 flex-shrink-0"
              />
            </div>
          </div>
          <div className="mt-8 text-center">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">{t('aboutUs.ourStory.title')}</h2>
            <div className="max-w-4xl mx-auto space-y-4 text-gray-700 leading-relaxed text-lg">
              <p>
                {t('aboutUs.ourStory.paragraph1')}
              </p>
            </div>
          </div>
        </section>

        {/* Mission */}
        <section className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('aboutUs.mission.title')}</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('aboutUs.mission.subtitle')}
            </p>
          </div>
        </section>

        {/* What We Organize */}
        <section className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">{t('aboutUs.whatWeOrganize.title')}</h2>
            <div className="max-w-4xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
                <div className="flex items-start space-x-3">
                  <div className="mt-1">
                    <CheckmarkIcon size={20} />
                  </div>
                  <p className="text-lg text-gray-700">{t('aboutUs.whatWeOrganize.groupTravel')}</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="mt-1">
                    <CheckmarkIcon size={20} />
                  </div>
                  <p className="text-lg text-gray-700">{t('aboutUs.whatWeOrganize.authenticExperiences')}</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="mt-1">
                    <CheckmarkIcon size={20} />
                  </div>
                  <p className="text-lg text-gray-700">{t('aboutUs.whatWeOrganize.wineTours')}</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="mt-1">
                    <CheckmarkIcon size={20} />
                  </div>
                  <p className="text-lg text-gray-700">{t('aboutUs.whatWeOrganize.customActivities')}</p>
                </div>
                <div className="flex items-start space-x-3 md:col-span-2">
                  <div className="mt-1">
                    <CheckmarkIcon size={20} />
                  </div>
                  <p className="text-lg text-gray-700">{t('aboutUs.whatWeOrganize.completeServices')}</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('aboutUs.whyChooseUs.title')}</h2>
            <p className="text-xl text-gray-600">
              {t('aboutUs.whyChooseUs.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center">
                <span className="text-xl">🏔️</span>
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2">{t('aboutUs.whyChooseUs.localExperience.title')}</h3>
                <p className="text-gray-700">
                  {t('aboutUs.whyChooseUs.localExperience.description')}
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-12 h-12 bg-green-600 text-white rounded-full flex items-center justify-center">
                <span className="text-xl">👥</span>
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2">{t('aboutUs.whyChooseUs.worryFreeTravel.title')}</h3>
                <p className="text-gray-700">
                  {t('aboutUs.whyChooseUs.worryFreeTravel.description')}
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-12 h-12 bg-orange-600 text-white rounded-full flex items-center justify-center">
                <span className="text-xl">🌱</span>
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2">{t('aboutUs.whyChooseUs.responsibleTourism.title')}</h3>
                <p className="text-gray-700">
                  {t('aboutUs.whyChooseUs.responsibleTourism.description')}
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center">
                <span className="text-xl">🌿</span>
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2">{t('aboutUs.values.sustainability.title')}</h3>
                <p className="text-gray-700">
                  {t('aboutUs.values.sustainability.description')}
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-12 h-12 bg-green-600 text-white rounded-full flex items-center justify-center">
                <span className="text-xl">🤝</span>
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2">{t('aboutUs.values.authenticity.title')}</h3>
                <p className="text-gray-700">
                  {t('aboutUs.values.authenticity.description')}
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-12 h-12 bg-orange-600 text-white rounded-full flex items-center justify-center">
                <span className="text-xl">⭐</span>
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2">{t('aboutUs.values.excellence.title')}</h3>
                <p className="text-gray-700">
                  {t('aboutUs.values.excellence.description')}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Customer Service Section */}
        <section className="mb-20">
          <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-2xl p-12 border border-green-200">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-green-600 text-white rounded-full flex items-center justify-center mr-4">
                    <span className="text-2xl">💬</span>
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold text-gray-900">{t('aboutUs.customerService.title')}</h2>
                    <p className="text-green-600 font-medium">{t('aboutUs.customerService.subtitle')}</p>
                  </div>
                </div>
                <p className="text-gray-700 text-lg leading-relaxed mb-6">
                  {t('aboutUs.customerService.description')}
                </p>
                <div className="flex items-center mb-8">
                  <div className="w-8 h-8 bg-green-100 text-green-600 rounded-full flex items-center justify-center mr-3">
                    <span className="text-sm">⚡</span>
                  </div>
                  <p className="text-green-700 font-medium">{t('aboutUs.customerService.responseTime')}</p>
                </div>
                <Link 
                  to="/contact" 
                  className="inline-flex items-center bg-green-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-green-700 transition-colors shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                >
                  <span className="mr-2">📞</span>
                  {t('aboutUs.customerService.contactUs')}
                </Link>
              </div>
              <div className="relative">
                <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-100">
                  <div className="flex items-center mb-6">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                      <span className="text-blue-600 text-xl">👨‍💼</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{t('aboutUs.customerService.supportTitle')}</h3>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-center text-sm text-gray-600">
                      <span className="w-2 h-2 bg-green-500 rounded-full mr-3"></span>
                      {t('aboutUs.customerService.veryFastAnswer')}
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <span className="w-2 h-2 bg-green-500 rounded-full mr-3"></span>
                      {t('aboutUs.customerService.personalizedAssistance')}
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <span className="w-2 h-2 bg-green-500 rounded-full mr-3"></span>
                      {t('aboutUs.customerService.followUp')}
                    </div>
                  </div>
                </div>
                <div className="absolute -top-4 -right-4 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm">✓</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="text-center bg-blue-600 text-white rounded-lg p-12">
          <h2 className="text-3xl font-bold mb-4">{t('aboutUs.cta.title')}</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            {t('aboutUs.cta.subtitle')}
          </p>
          
          {/* First row - Single button */}
          <div className="mb-6">
            <Link 
              to="/#trips" 
              className="inline-block bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              {t('aboutUs.cta.discoverTrips')}
            </Link>
          </div>
          
          {/* Second row - Three buttons in a row (desktop) / stacked (mobile) */}
          <div className="flex flex-col md:flex-row justify-center items-center gap-4 md:space-x-4 md:gap-0">
            <Link 
              to="/#destinations" 
              className="w-full md:w-auto inline-block border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors text-center"
            >
              {t('aboutUs.cta.exploreDestinations')}
            </Link>
            <Link 
              to="/#activities" 
              className="w-full md:w-auto inline-block border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors text-center"
            >
              {t('aboutUs.cta.exploreActivities')}
            </Link>
            <Link 
              to="/#curiosities" 
              className="w-full md:w-auto inline-block border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors text-center"
            >
              {t('aboutUs.cta.exploreCuriosities')}
            </Link>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  )
}

export default AboutPage 